import router from "@/router";
import {MarkerModule} from "@/store/modules/marker";
import {Marker} from "@/data/classes/marker";
import {getValue, goToFireStore} from "@/globalFunctions";
import {CustomerModule} from "@/store/modules/customer";
import {MessageBox} from "element-ui";
import i18n from "@/lang";
import {UserModule} from "@/store/modules/user";

export const tutorialLink = "marker-overview";

export const filters = [
    {
        field:"active",
        clearable: true,
        options: [
            { key: true, label: 'Active' },
            { key: false, label: 'Inactive' }
        ],
        placeholder: 'table.status'
    },
    {
        field:"customerId",
        clearable: true,
        options: CustomerModule.list.map((customer) => {
            return {
                key: customer.Id,
                label: customer?.Company?.Name
            }
        }),
        placeholder: 'table.customerId'
    },{
        field: "sort",
        clearable: true,
        options: [
            { label: 'table.order.id-asc', key: 'Id-asc' },
            { label: 'table.order.id-desc', key: 'Id-desc' },
            { label: 'table.order.created-asc', key: 'CreatedDateTime-asc' },
            { label: 'table.order.created-desc', key: 'CreatedDateTime-desc' },
            { label: 'table.order.updated-asc', key: 'DocumentUpdatedDateTime-asc' },
            { label: 'table.order.updated-desc', key: 'DocumentUpdatedDateTime-desc' },
        ],
        placeholder: 'table.sort'
    }
];

export const columns = [
    {
        label: 'table.image',
        field: "FileUrl",
        type: "image",
        maxImageHeight: "120px",
        maxImageWidth: "120px"
    },
    {
        label: 'table.displayName',
        field: "DisplayName",
    },
    {
        label: 'table.status',
        field: "Active",
        translate: true,
        formatter: (x : any) => {
            return (x ? 'table.active' : 'table.inactive');
        },
        tag: true,
        tagType: (x : any) => {
            return (x ? 'success' : 'danger')
        }
    },
    {
        label: 'table.updated',
        field: 'DocumentUpdatedDateTime',
        formatter: (x : any) => {
            return new Date(x).toLocaleString();
        }
    }
];

export const actions = [
    {
        label: 'table.edit',
        icon: "edit",
        type: 'info',
        click: (row : any) => {
            router.push("/marker/editor/" + row.Id);
        }
    },
    {
        label: 'table.activate',
        icon: "toggle_off",
        type: 'default',
        condition: (row: any) => {
            return !row.Active;
        },
        click: (row : any) => {
            window.GlobalEvents.$emit("start-list-load");
            return MarkerModule.list.activate([row.Id]).then((result) => {
                if (result.success.length > 0){
                    window.GlobalEvents.$emit("notify", {
                        type: "success",
                        title: i18n.t("notifications.marker-activation.success.title"),
                        message: i18n.t("notifications.marker-activation.success.message")
                    });
                } else {
                    window.GlobalEvents.$emit("notify", {
                        type: "error",
                        title: i18n.t("notifications.marker-activation.error.title"),
                        message: i18n.t("notifications.marker-activation.error.message")
                    });
                }
                window.GlobalEvents.$emit("refresh-list");
            }).catch(() => {
                window.GlobalEvents.$emit("notify", {
                    type: "error",
                    title: i18n.t("notifications.marker-activation.error.title"),
                    message: i18n.t("notifications.marker-activation.error.message")
                });
            })
        }
    },
    {
        label: 'table.deactivate',
        icon: "toggle_on",
        type: 'primary',
        condition: (row: any) => {
            return row.Active;
        },
        click: (row : any) => {
            window.GlobalEvents.$emit("start-list-load");
            return MarkerModule.list.deactivate([row.Id]).then((result) => {
                if (result.error.length === 0){
                    window.GlobalEvents.$emit("notify", {
                        type: "success",
                        title: i18n.t("notifications.marker-deactivation.success.title"),
                        message: i18n.t("notifications.marker-deactivation.success.message")
                    });
                } else {
                    window.GlobalEvents.$emit("notify", {
                        type: "error",
                        title: i18n.t("notifications.marker-deactivation.error.title"),
                        message: i18n.t("notifications.marker-deactivation.error.message")
                    });
                }
                window.GlobalEvents.$emit("refresh-list");
            }).catch((error) => {
                console.error(error);
                window.GlobalEvents.$emit("notify", {
                    type: "error",
                    title: i18n.t("notifications.marker-deactivation.error.title"),
                    message: i18n.t("notifications.marker-deactivation.error.message")
                });
            })
        }
    },
    {
        label: 'table.to-firestore',
        icon: "local_fire_department",
        type: 'warning',
        condition: () => {
            return UserModule.roles.includes("admin");
        },
        click: (row : any) => {
            goToFireStore({
                type: "marker",
                markerId: row.Id
            });
        }
    },
    {
        label: 'table.delete',
        icon: 'delete',
        type: 'danger',
        click: (row : any) => {
            MessageBox({
                title: i18n.t('confirmations.delete-marker.title').toString(),
                message: i18n.t('confirmations.delete-marker.text').toString(),
                showCancelButton: true,
                confirmButtonClass: "el-button--info",
                cancelButtonClass: "el-button--danger",
                confirmButtonText: i18n.t('confirmations.default.confirmation-button').toString(),
                cancelButtonText: i18n.t('confirmations.default.cancel-button').toString(),
            }).then(async () => {
                window.GlobalEvents.$emit("start-list-load");
                return MarkerModule.list.remove(row.Id).then(() => {
                    window.GlobalEvents.$emit("notify", {
                        type: "success",
                        title: i18n.t("notifications.delete-marker.success.title"),
                        message: i18n.t("notifications.delete-marker.success.message")
                    });
                    window.GlobalEvents.$emit("refresh-list");
                }).catch(() => {
                    window.GlobalEvents.$emit("notify", {
                        type: "error",
                        title: i18n.t("notifications.delete-marker.error.title"),
                        message: i18n.t("notifications.delete-marker.error.message")
                    });
                })
            })
        }
    }
];

export const getData = (listQuery : any) => {
    if (!CustomerModule.list._retrieved){
        CustomerModule.list.get(UserModule.license);
    }

    // Get items
    let items = MarkerModule.list as Array<Marker>;

    // No archived
    items = items.filter(x => !x.Archived);

    // Query
    if (listQuery.customerId){
        items = items.filter(x => x.CustomerId === listQuery.customerId);
    }

    // Query
    if (listQuery.active === true || listQuery.active === false){
        items = items.filter(x => x.Active === listQuery.active);
    }

    // Sort
    if (listQuery.sort){
        const [field, order] = listQuery.sort.split("-")
        items = items.sort((a: any, b: any) => {
            if(getValue(a, field) < getValue(b, field)) { return order === 'asc' ? -1 : 1; }
            if(getValue(a, field) > getValue(b, field)) { return order === 'asc' ? 1 : -1; }
            return 0;
        });
    }

    return {
        data: {
            items: items,
            total: items.length
        }
    };
};
